import { environment } from 'src/environments/environment';

/**
 * Builds Amplify Auth config from the active environment.
 * Supports Cognito username/password and Hosted UI OAuth (Okta SAML federation).
 */
export function buildAmplifyConfig(): Record<string, unknown> {
  const amplify = normalizeAmplify(environment.AMPLIFY);
  const region = amplify.region || 'us-east-2';
  const userPoolId = amplify.userPoolId || '';
  const userPoolClientId =
    amplify.userPoolClientId || amplify.userPoolWebClientId || '';

  const loginWith: Record<string, unknown> = {
    username: true,
    email: true,
  };

  const cognitoDomain = (environment as any).cognitoDomain as string | undefined;
  if (cognitoDomain) {
    loginWith['oauth'] = {
      domain: cognitoDomain,
      // Must match Cognito app client Allowed OAuth scopes.
      // Do not request aws.cognito.signin.user.admin unless that scope is
      // enabled on the app client — Cognito returns invalid_scope otherwise.
      scopes: ['openid', 'email', 'profile'],
      redirectSignIn: buildRedirectSignInUrls(),
      redirectSignOut: buildRedirectSignOutUrls(),
      responseType: 'code',
    };
  }

  return {
    Auth: {
      Cognito: {
        region,
        userPoolId,
        userPoolClientId,
        loginWith,
        authenticationFlowType:
          amplify.authenticationFlowType || 'USER_PASSWORD_AUTH',
      },
    },
  };
}

export function getSsoProviderName(): string {
  return ((environment as any).ssoProviderName as string) || 'DU-SSO-SCRAII';
}

/**
 * Cognito SAML IdP from the email domain:
 * dataunveil.com → DU-SSO-SCRAII
 * acme.com → ACME-SSO-SCRAII (name without TLD, uppercased)
 */
export function getSsoProviderNameForEmail(email: string): string {
  const trimmed = (email || '').trim();
  const at = trimmed.lastIndexOf('@');
  if (at < 0 || at === trimmed.length - 1) {
    return getSsoProviderName();
  }
  const domain = trimmed.slice(at + 1).toLowerCase();
  if (domain === 'dataunveil.com') {
    return 'DU-SSO-SCRAII';
  }
  const labels = domain.split('.').filter(Boolean);
  const withoutTld =
    labels.length >= 2 ? labels.slice(0, -1).join('.') : domain;
  return `${withoutTld.toUpperCase()}-SSO-SCRAII`;
}

export function isSsoConfigured(): boolean {
  return !!((environment as any).cognitoDomain as string);
}

function normalizeAmplify(raw: any): {
  region?: string;
  userPoolId?: string;
  userPoolClientId?: string;
  userPoolWebClientId?: string;
  authenticationFlowType?: string;
} {
  if (!raw) {
    return {};
  }
  // environment.ts nests under AMPLIFY.Auth
  if (raw.Auth) {
    return {
      region: raw.Auth.region || raw.region,
      userPoolId: raw.Auth.userPoolId || raw.userPoolId,
      userPoolClientId:
        raw.Auth.userPoolClientId ||
        raw.Auth.userPoolWebClientId ||
        raw.userPoolClientId ||
        raw.userPoolWebClientId,
      userPoolWebClientId:
        raw.Auth.userPoolWebClientId || raw.userPoolWebClientId,
      authenticationFlowType:
        raw.Auth.authenticationFlowType || raw.authenticationFlowType,
    };
  }
  return raw;
}

function buildRedirectSignInUrls(): string[] {
  const urls = new Set<string>();

  // Prefer the current origin so Amplify's redirect_uri matches the Cognito authorize request.
  if (typeof window !== 'undefined' && window.location?.origin) {
    urls.add(`${window.location.origin}/callback`);
  }

  urls.add('http://localhost:4200/callback');
  urls.add('http://127.0.0.1:4200/callback');

  const redirectUri = (environment.redirectUri || '').replace(/\/$/, '');
  if (
    redirectUri &&
    !redirectUri.includes('api.') &&
    typeof window !== 'undefined' &&
    window.location?.hostname !== 'localhost' &&
    window.location?.hostname !== '127.0.0.1'
  ) {
    urls.add(`${redirectUri}/callback`);
  }

  return Array.from(urls);
}

function buildRedirectSignOutUrls(): string[] {
  const urls = new Set<string>();

  if (typeof window !== 'undefined' && window.location?.origin) {
    urls.add(window.location.origin);
  }

  urls.add('http://localhost:4200');
  urls.add('http://127.0.0.1:4200');

  const redirectUri = (environment.redirectUri || '').replace(/\/$/, '');
  if (
    redirectUri &&
    !redirectUri.includes('api.') &&
    typeof window !== 'undefined' &&
    window.location?.hostname !== 'localhost' &&
    window.location?.hostname !== '127.0.0.1'
  ) {
    urls.add(redirectUri);
  }

  return Array.from(urls);
}
